import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from '@lwt-helix/modal';
import { Button } from '@lwt-helix/buttons';
import Select from '@lwt-helix/select';
import { editCriteriaTerms, areaSelectDropdown } from '../../constants/suggestedAgents';
import { modules } from '../../../constants/index';
import { useCommonStore } from '../../../store/store';
import { getTimeFrameTitle } from '../../../common/helpers/titles';
import { getTimePeriodsByModule } from '../../../common/helpers/timePeriods';
import { agentProductionTerms, TimePeriods } from '../../../constants';
import { useMls } from '../../../common/hooks/mls';
import PropertyTypeDropdown from './PropertyTypeDropdown';
import AreaSelectDropdown from './AreaSelectDropdown';

const EditCriteriaModal = ({ show, onClose }) => {
    const [formData, setFormData] = useState({
        mls: null,
        timePeriod: null,
        volume: null,
        units: null,
        propertyTypes: null,
        areaType: null,
        area: null,
    });

    const closeDropdownRef = useRef(null);

    const { mlsProviders, timeIntervals } = useCommonStore(state => ({
        mlsProviders: state.mlsProviders || [],
        timeIntervals: state.timeIntervals,
    }));

    const mlsOptions = useMemo(() => {
        return mlsProviders
            .map(mls => ({
                value: mls.mlsId,
                label: `${mls.shortDescription} (${mls.longDescription})`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [mlsProviders]);

    const defaultMls = mlsOptions[0] || null;
    const selectedMls = formData.mls ?? defaultMls;
    const mlsProps = useMls(selectedMls?.value, modules.proficiencyMetrics.suggestedAgents, { isSilentLoad: true });
    const mlsPropertyTypes = useMemo(() => mlsProps?.propTypes || [], [mlsProps?.propTypes]);
    const areasByType = useMemo(() => mlsProps?.mappedAreas || {}, [mlsProps?.mappedAreas]);
    const isMarketAreaLoading = !!selectedMls?.value && !mlsProps?.mappedAreas;

    const allPropertyTypeIds = useMemo(() => {
        return mlsPropertyTypes.flatMap(propertyType =>
            propertyType.types?.map(t => Number(t.typeId)) || []
        );
    }, [mlsPropertyTypes]);

    const timePeriodOptions = useMemo(() => {
        const options = selectedMls && mlsProviders && getTimePeriodsByModule({
            module: modules.proficiencyMetrics.suggestedAgents,
            selectedMlsId: selectedMls?.value,
            mlsMembershipList: mlsProviders
        });
        
        return options?.filter(opt => opt.value !== TimePeriods['Custom Date']) || [];
    }, [selectedMls, mlsProviders]);

    const defaultTimePeriod =
        timePeriodOptions.find(opt => opt.value === agentProductionTerms.annually)
        || timePeriodOptions[0]
        || null;
    const selectedTimePeriod = formData.timePeriod ?? defaultTimePeriod;
    const selectedPropertyTypes = formData.propertyTypes ?? allPropertyTypeIds;

    const areaTypeOptions = useMemo(() => {
        return Object.entries(areasByType)
            .filter(([, data]) => (data?.mappedAreas?.length ?? 0) > 0)
            .map(([type]) => ({
                value: type,
                label: type.charAt(0).toUpperCase() + type.slice(1),
            }));
    }, [areasByType]);

    const defaultAreaType =
        areaTypeOptions.find(opt => opt.value === areaSelectDropdown.defaultAreaType)
        || areaTypeOptions[0]
        || null;
    const selectedAreaType = formData.areaType ?? defaultAreaType;

    const areaOptions = useMemo(() => {
        if (!selectedAreaType?.value) return [];
        const areas = areasByType[selectedAreaType.value]?.mappedAreas || [];
        return [...areas]
            .sort((a, b) => (b.soldCount ?? 0) - (a.soldCount ?? 0))
            .map(area => ({
                value: area.areaValue,
                label: `${area.areaValue} (${(area.soldCount ?? 0).toLocaleString()})`,
                soldCount: area.soldCount,
            }));
    }, [selectedAreaType, areasByType]);

    const defaultArea = areaOptions[0] || null;
    const selectedArea = formData.area ?? defaultArea;

    const handleSelectChange = (field, value) => {
        if (field === editCriteriaTerms.mlsKey) {
            setFormData(prev => ({
                ...prev,
                [field]: value,
                timePeriod: null,
                propertyTypes: null,
                areaType: null,
                area: null,
            }));
            return;
        }
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAreaTypeChange = (value) => {
        setFormData(prev => ({
            ...prev,
            areaType: value,
            area: null,
        }));
    };

    const handlePropertyTypesChange = (selectedPropertyTypes) => {
        setFormData(prev => ({ ...prev, propertyTypes: selectedPropertyTypes.map(id => Number(id)) }));
    };

    const handleSave = () => {
        onClose();
    };

    const handleCancel = () => {
        setFormData({
            mls: null,
            timePeriod: null,
            volume: null,
            units: null,
            propertyTypes: null,
            areaType: null,
            area: null,
        });
        onClose();
    };

    const formatTimePeriodLabel = (option, { context }) => {
        if (context === 'value' && timeIntervals) {
            const titleObj = getTimeFrameTitle(
                { intervalType: option.value },
                timeIntervals
            );
            
            if (titleObj?.subtitle) {
                return `${option.label}: ${titleObj.subtitle}`;
            }
        }
        return option.label;
    };

    /**
     * Mock data for Edit Criteria Modal.
     * TODO: Replace with real data from store.
     */
    const volumeOptions = [{ value: 'low', label: '0 - 1M' }, { value: 'medium', label: '1M - 5M' }, { value: 'high', label: '5M+' }];
    const unitsOptions = [{ value: 'low', label: '0 - 10' }, { value: 'medium', label: '10 - 50' }, { value: 'high', label: '50+' }];

    return (
        <Modal
            dataLwtId="edit-criteria-modal"
            show={show}
            onClose={onClose}
            title={editCriteriaTerms.editCriteria}
            size="md"
            showFooter={true}
            buttons={
                <>
                    <Button
                        className="helix-text-gray-500 text-dark"
                        onClick={handleCancel}
                    >
                        {editCriteriaTerms.cancel}
                    </Button>
                    <Button
                        className="helix-bg-brand persist-bg text-white"
                        onClick={handleSave}
                    >
                        {editCriteriaTerms.save}
                    </Button>
                </>
            }
        >
            <div>
                <div className="mb-3">
                    <label className="d-block font-weight-bold mb-1">{editCriteriaTerms.mls}</label>
                    <Select
                        dataLwtId="edit-criteria-mls"
                        placeholder={editCriteriaTerms.selectMls}
                        value={selectedMls}
                        onChange={(value) => handleSelectChange(editCriteriaTerms.mlsKey, value)}
                        options={mlsOptions}
                        isClearable={false}
                        onMenuOpen={() => closeDropdownRef.current?.()}
                    />
                </div>

                <div className="mb-3">
                    <label className="d-block font-weight-bold mb-1">{editCriteriaTerms.marketArea}</label>
                    <div className="row gap-1">
                        <div className="col-4">
                            <Select
                                dataLwtId="edit-criteria-area-type"
                                value={selectedAreaType}
                                onChange={handleAreaTypeChange}
                                options={areaTypeOptions}
                                isClearable={false}
                                isDisabled={isMarketAreaLoading}
                                onMenuOpen={() => closeDropdownRef.current?.()}
                            />
                        </div>

                        <div className="col-8">
                            <AreaSelectDropdown
                                dataLwtId="edit-criteria-area"
                                value={selectedArea}
                                onChange={(value) => handleSelectChange(editCriteriaTerms.areaKey, value)}
                                options={areaOptions}
                                isDisabled={isMarketAreaLoading}
                                onMenuOpen={() => closeDropdownRef.current?.()}
                            />
                        </div>
                    </div>
                </div>

                <div className="mb-3">
                    <label className="d-block font-weight-bold mb-1">{editCriteriaTerms.timePeriod}</label>
                    <Select
                        dataLwtId="edit-criteria-time-period"
                        placeholder={editCriteriaTerms.selectTimePeriod}
                        value={selectedTimePeriod}
                        onChange={(value) => handleSelectChange(editCriteriaTerms.timePeriodKey, value)}
                        options={timePeriodOptions}
                        formatOptionLabel={formatTimePeriodLabel}
                        isClearable={false}
                        onMenuOpen={() => closeDropdownRef.current?.()}
                    />
                </div>

                <div className="row mb-3">
                    <div className="col-6">
                        <label className="d-block font-weight-bold mb-1">{editCriteriaTerms.volume}</label>
                        <Select
                            dataLwtId="edit-criteria-volume"
                            placeholder={editCriteriaTerms.selectVolume}
                            value={formData.volume}
                            onChange={(value) => handleSelectChange(editCriteriaTerms.volumeKey, value)}
                            options={volumeOptions}
                            isClearable={false}
                            onMenuOpen={() => closeDropdownRef.current?.()}
                        />
                    </div>

                    <div className="col-6">
                        <label className="d-block font-weight-bold mb-1">{editCriteriaTerms.units}</label>
                        <Select
                            dataLwtId="edit-criteria-units"
                            placeholder={editCriteriaTerms.selectUnits}
                            value={formData.units}
                            onChange={(value) => handleSelectChange(editCriteriaTerms.unitsKey, value)}
                            options={unitsOptions}
                            isClearable={false}
                            onMenuOpen={() => closeDropdownRef.current?.()}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="d-block font-weight-bold mb-1">{editCriteriaTerms.propertyTypes}</label>
                    <PropertyTypeDropdown
                        key={`${selectedMls?.value}-${allPropertyTypeIds.length}`}
                        mlsPropertyTypes={mlsPropertyTypes}
                        selectedPropertyTypes={selectedPropertyTypes}
                        onPropertyTypesChange={handlePropertyTypesChange}
                        onRegisterClose={(fn) => { closeDropdownRef.current = fn; }}
                    />
                </div>

            </div>
        </Modal>
    );
};

EditCriteriaModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default EditCriteriaModal;
